(function () {
  'use strict';

  if (window.UttamCaseStudyRuntime) {
    window.UttamCaseStudyRuntime.scan();
    return;
  }

  const instances = new WeakMap();

  function init(root) {
    if (!root || instances.has(root)) return;
    root.classList.add('ue-js');
    const state = { cleanups: [], frame: null, mode: null };
    instances.set(root, state);
    const addCleanup = (cleanup) => state.cleanups.push(cleanup);
    const on = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      addCleanup(() => target.removeEventListener(event, handler, options));
    };

    const revealItems = root.querySelectorAll('.ue-reveal');
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver((entries, instance) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('ue-is-visible');
          instance.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
      revealItems.forEach((item) => observer.observe(item));
      addCleanup(() => observer.disconnect());
    } else revealItems.forEach((item) => item.classList.add('ue-is-visible'));

    const videos = Array.from(root.querySelectorAll('video'));
    videos.forEach((video) => on(video, 'play', () => {
      videos.forEach((other) => { if (other !== video && !other.paused) other.pause(); });
    }));

    const dialog = root.querySelector('.ue-case__dialog');
    const dialogImage = root.querySelector('[data-ue-dialog-image]');
    const closeButton = root.querySelector('[data-ue-dialog-close]');
    let returnFocus;
    const closeDialog = () => {
      if (!dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      if (returnFocus) returnFocus.focus();
    };
    root.querySelectorAll('[data-ue-artwork]').forEach((trigger) => on(trigger, 'click', (event) => {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      returnFocus = trigger;
      dialogImage.src = trigger.dataset.ueArtwork;
      dialogImage.alt = trigger.dataset.ueAlt || '';
      dialog.showModal();
      closeButton?.focus();
    }));
    if (closeButton) on(closeButton, 'click', closeDialog);
    if (dialog) {
      on(dialog, 'click', (event) => { if (event.target === dialog) closeDialog(); });
      on(dialog, 'cancel', (event) => { event.preventDefault(); closeDialog(); });
    }

    const hero = root.querySelector('[data-ue-hero]');
    const reveal = hero?.querySelector('[data-ue-reveal]');
    const tilt = hero?.querySelector('[data-ue-tilt]');
    const media = tilt?.closest('.ue-hero__media');
    if (!hero || !reveal || !tilt || !media) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const narrowViewport = window.matchMedia('(max-width: 1000px)');
    let entranceObserver;
    let visibilityObserver;
    let heroInView = true;
    let pointerFrame = null;
    let scrollFrame = null;
    let pointerTarget = { x: 0, y: 0 };
    const readNumber = (name, fallback) => {
      const value = parseFloat(getComputedStyle(root).getPropertyValue(name));
      return Number.isNaN(value) ? fallback : value;
    };
    const tiltMax = readNumber('--ue-tilt-max', 4);
    const depthMax = readNumber('--ue-depth-max', 18);
    const scrollTravel = readNumber('--ue-scroll-travel', 22);
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const revealEntrance = () => window.requestAnimationFrame(() => window.requestAnimationFrame(() => reveal.classList.add('is-revealed')));

    const applyPointer = () => {
      pointerFrame = null;
      tilt.style.setProperty('--ue-tilt-x', `${(-pointerTarget.y * tiltMax).toFixed(3)}deg`);
      tilt.style.setProperty('--ue-tilt-y', `${(pointerTarget.x * tiltMax).toFixed(3)}deg`);
      const distance = Math.min(1, Math.hypot(pointerTarget.x, pointerTarget.y));
      tilt.style.setProperty('--ue-depth', `${(depthMax * (1 - distance * 0.55)).toFixed(2)}px`);
    };
    const onPointerMove = (event) => {
      const rect = media.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      pointerTarget.x = clamp(((event.clientX - rect.left) / rect.width - 0.5) * 2, -1, 1);
      pointerTarget.y = clamp(((event.clientY - rect.top) / rect.height - 0.5) * 2, -1, 1);
      if (pointerFrame === null) pointerFrame = window.requestAnimationFrame(applyPointer);
    };
    const onPointerEnter = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      tilt.classList.add('is-tracking');
      onPointerMove(event);
    };
    const onPointerLeave = () => {
      if (pointerFrame !== null) window.cancelAnimationFrame(pointerFrame);
      pointerFrame = null;
      tilt.classList.remove('is-tracking');
      tilt.style.setProperty('--ue-tilt-x', '0deg');
      tilt.style.setProperty('--ue-tilt-y', '0deg');
      tilt.style.setProperty('--ue-depth', '0px');
    };
    const bindPointer = () => {
      media.addEventListener('pointerenter', onPointerEnter);
      media.addEventListener('pointermove', onPointerMove);
      media.addEventListener('pointerleave', onPointerLeave);
    };
    const unbindPointer = () => {
      media.removeEventListener('pointerenter', onPointerEnter);
      media.removeEventListener('pointermove', onPointerMove);
      media.removeEventListener('pointerleave', onPointerLeave);
      onPointerLeave();
    };
    const computeScrollShift = () => {
      const rect = hero.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const progress = clamp((viewport - rect.bottom) / viewport, 0, 1);
      tilt.style.setProperty('--ue-shift', `${((0.5 - progress) * scrollTravel).toFixed(2)}px`);
    };
    const applyScroll = () => { scrollFrame = null; computeScrollShift(); };
    const onScroll = () => {
      if (!heroInView || scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(applyScroll);
    };
    const bindScroll = () => {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      window.addEventListener('orientationchange', onScroll, { passive: true });
      computeScrollShift();
    };
    const unbindScroll = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('orientationchange', onScroll);
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
      tilt.style.setProperty('--ue-shift', '0px');
    };
    const wantsScroll = () => narrowViewport.matches || !finePointer.matches;
    const syncMode = () => {
      const nextMode = wantsScroll() ? 'scroll' : 'pointer';
      if (nextMode === state.mode) return;
      if (state.mode === 'pointer') unbindPointer();
      if (state.mode === 'scroll') unbindScroll();
      if (nextMode === 'pointer') bindPointer();
      else bindScroll();
      state.mode = nextMode;
    };

    if (!reduceMotion.matches) {
      root.classList.add('ue-hero-motion');
      if ('IntersectionObserver' in window) {
        entranceObserver = new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          revealEntrance();
          observer.disconnect();
        }, { threshold: 0.2 });
        entranceObserver.observe(reveal);
        visibilityObserver = new IntersectionObserver((entries) => { entries.forEach((entry) => { heroInView = entry.isIntersecting; }); }, { rootMargin: '20% 0px 20% 0px', threshold: 0 });
        visibilityObserver.observe(hero);
      } else revealEntrance();
      syncMode();
      [finePointer, narrowViewport].forEach((query) => {
        const listener = () => syncMode();
        if (typeof query.addEventListener === 'function') query.addEventListener('change', listener);
        else query.addListener(listener);
        addCleanup(() => {
          if (typeof query.removeEventListener === 'function') query.removeEventListener('change', listener);
          else query.removeListener(listener);
        });
      });
    } else reveal.classList.add('is-revealed');

    const onReducedMotionChange = () => {
      if (!reduceMotion.matches) return;
      if (state.mode === 'pointer') unbindPointer();
      if (state.mode === 'scroll') unbindScroll();
      state.mode = null;
      reveal.classList.add('is-revealed');
      root.classList.remove('ue-hero-motion');
    };
    if (typeof reduceMotion.addEventListener === 'function') on(reduceMotion, 'change', onReducedMotionChange);
    else if (typeof reduceMotion.addListener === 'function') {
      reduceMotion.addListener(onReducedMotionChange);
      addCleanup(() => reduceMotion.removeListener(onReducedMotionChange));
    }

    addCleanup(() => {
      if (state.mode === 'pointer') unbindPointer();
      if (state.mode === 'scroll') unbindScroll();
      if (entranceObserver) entranceObserver.disconnect();
      if (visibilityObserver) visibilityObserver.disconnect();
      if (pointerFrame !== null) window.cancelAnimationFrame(pointerFrame);
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      root.classList.remove('ue-js', 'ue-hero-motion');
    });
  }

  function destroy(root) {
    const state = instances.get(root);
    if (!state) return;
    state.cleanups.reverse().forEach((cleanup) => cleanup());
    instances.delete(root);
  }

  function scan(scope = document) {
    if (scope.matches?.('#uttam-case-study')) init(scope);
    scope.querySelectorAll?.('#uttam-case-study').forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('#uttam-case-study') ? event.target : event.target.querySelector?.('#uttam-case-study');
    if (root) destroy(root);
  });
  window.UttamCaseStudyRuntime = { init, destroy, scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
})();
